/**
 * FE-G1-4 — group page (#/group) server-scoped fleet load + load-more (DEF-FE-G1-004).
 */

import {
  fetchFleetDevicesList,
  fetchFleetDevicesPage,
  fetchFleetDevicesPoll,
  fetchGroupDevicesPoll,
  FLEET_DEVICES_INITIAL_PAGES,
  GROUP_DEVICES_POLL_MAX_PAGES,
} from "./fleet-list-paginated.js";
import {
  devicesSearchQueryReady,
} from "./devices-fleet-search.js";
import { deviceSharedGroupKey } from "./overview-group-slot-helpers.js";

/** Virtual group key for full-fleet view at #/group/_all */
export const GROUP_FLEET_ALL_KEY = "_all";

/** Virtual group key for #/groups directory (Mode B sidebar hub; no single-group fleet). */
export const GROUP_HUB_DIRECTORY_KEY = "__groups__";

/**
 * @param {string} groupKey
 * @param {(v: unknown) => string} canonicalGroupKeyFn
 */
export function isGroupFleetAllView(groupKey, canonicalGroupKeyFn) {
  return canonicalGroupKeyFn(String(groupKey || "")) === canonicalGroupKeyFn(GROUP_FLEET_ALL_KEY);
}

/**
 * @param {string} groupKey
 * @param {(v: unknown) => string} canonicalGroupKeyFn
 */
export function isGroupHubDirectoryView(groupKey, canonicalGroupKeyFn) {
  return canonicalGroupKeyFn(String(groupKey || "")) === canonicalGroupKeyFn(GROUP_HUB_DIRECTORY_KEY);
}

/**
 * Group page first paint: **one** cursor page (KILL-MERGE). deeper pages via Load more.
 */
export const GROUP_PAGE_DEVICES_INITIAL_PAGES = GROUP_DEVICES_POLL_MAX_PAGES;

/**
 * @param {object[]} items
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string }} ctx
 * @returns {object[]}
 */
export function filterFleetItemsForGroupPage(items, groupKey, tenantOwner, ctx) {
  const g = String(groupKey || "").trim();
  if (!g) return Array.isArray(items) ? items : [];
  return (Array.isArray(items) ? items : []).filter((d) => {
    if (deviceSharedGroupKey(d, ctx.canonicalGroupKey) !== g) return false;
    if (ctx.me && ctx.me.role === "superadmin" && tenantOwner) {
      return String(d.owner_admin || "").trim() === tenantOwner;
    }
    if (tenantOwner) {
      const devOwner = String(d.owner_admin || "").trim() || String(d.shared_by || "").trim();
      return devOwner === tenantOwner;
    }
    return true;
  });
}

/**
 * @param {{ groupKey: string, ownerAdmin?: string, live?: boolean, timeoutMs?: number, retries?: number, maxPages?: number, ctx: object }} opts
 */
export async function loadGroupPageFleet(opts) {
  const groupKey = String(opts.groupKey || "").trim();
  const maxPages = opts.maxPages != null ? opts.maxPages : GROUP_PAGE_DEVICES_INITIAL_PAGES;
  if (isGroupHubDirectoryView(groupKey, opts.ctx.canonicalGroupKey)) {
    return {
      items: [],
      pages_fetched: 0,
      page: { has_more: false, next_cursor: null },
    };
  }
  if (isGroupFleetAllView(groupKey, opts.ctx.canonicalGroupKey)) {
    const searchQ = opts.searchQ != null ? String(opts.searchQ).trim() : "";
    const apiSearchQ = devicesSearchQueryReady(searchQ) ? searchQ : "";
    // First paint: one page only (same as #/devices). Walking 20×limit hangs ~10k fleets.
    const allCap = opts.maxPages != null ? opts.maxPages : FLEET_DEVICES_INITIAL_PAGES;
    return fetchFleetDevicesList({
      live: !!opts.live,
      timeoutMs: opts.timeoutMs,
      retries: opts.retries,
      maxPages: allCap,
      searchQ: apiSearchQ || null,
    });
  }
  const pollOpts = {
    groupKey,
    ownerAdmin: opts.ownerAdmin,
    live: !!opts.live,
    timeoutMs: opts.timeoutMs,
    retries: opts.retries,
    maxPages,
  };
  try {
    return await fetchGroupDevicesPoll(pollOpts);
  } catch (err) {
    const msg = String((err && err.message) || err || "");
    if (!msg.startsWith("400")) throw err;
    const legacy = await fetchFleetDevicesPoll({
      live: pollOpts.live,
      timeoutMs: pollOpts.timeoutMs,
      retries: pollOpts.retries,
      maxPages,
    });
    const filtered = filterFleetItemsForGroupPage(
      legacy.items,
      groupKey,
      String(opts.ownerAdmin || "").trim(),
      opts.ctx,
    );
    return { ...legacy, items: filtered, _legacyClientFilter: true };
  }
}

/**
 * @param {{ groupKey: string, ownerAdmin?: string, cursor: string, live?: boolean, timeoutMs?: number, retries?: number }} opts
 */
export async function appendGroupPageFleetPage(opts) {
  const cursor = String(opts.cursor || "").trim();
  if (!cursor) {
    return { items: [], page: null, paginated: false };
  }
  const searchQ = opts.searchQ != null ? String(opts.searchQ).trim() : "";
  const apiSearchQ = devicesSearchQueryReady(searchQ) ? searchQ : "";
  if (opts.ctx && isGroupFleetAllView(opts.groupKey, opts.ctx.canonicalGroupKey)) {
    return fetchFleetDevicesPage({
      cursor,
      live: !!opts.live,
      timeoutMs: opts.timeoutMs,
      retries: opts.retries,
      searchQ: apiSearchQ || null,
    });
  }
  return fetchFleetDevicesPage({
    groupKey: opts.groupKey,
    ownerAdmin: opts.ownerAdmin,
    cursor,
    live: !!opts.live,
    timeoutMs: opts.timeoutMs,
    retries: opts.retries,
    searchQ: apiSearchQ || null,
  });
}

/**
 * @param {object | null | undefined} list
 * @param {object | null | undefined} page
 * @returns {{ nextCursor: string | null, hasMore: boolean }}
 */
export function groupPageFleetCursorState(list, page) {
  const pg = page && typeof page === "object" ? page : (list && list.page) || null;
  const nextCursor = pg && pg.next_cursor != null ? String(pg.next_cursor) : null;
  const hasMore = !!(pg && pg.has_more) || !!(list && list.truncated);
  return { nextCursor, hasMore };
}

/**
 * @param {object | null | undefined} list
 * @param {string} [bannerId]
 * @returns {string}
 */
export function groupPageLegacyFilterBannerHtml(list, bannerId = "grpLegacyFilterBanner") {
  if (!(list && list._legacyClientFilter)) return "";
  return (
    `<p class="badge offline" id="${bannerId}" data-legacy-client-filter="1" style="margin:8px 0">` +
    `This group list is incomplete on this page. ` +
    `Counts may be short — use All boards or Groups.` +
    `</p>`
  );
}
